import { ALLOWED_CONTENT_TYPES, MAX_FILE_BYTES, type MediaCategory } from './types';

const MB = 1024 * 1024;

/** content-type → 사용자 표기(예: image/jpeg → JPEG). 미지의 타입은 서브타입을 대문자화. */
const labelForContentType = (contentType: string): string => {
  const subtype = contentType.split('/')[1] ?? contentType;
  return subtype === 'jpeg' ? 'JPEG' : subtype.toUpperCase();
};

/**
 * 전 이미지 업로드 섹션 공통 포맷/용량 가이드(ID57). 클라이언트 검증 상수
 * (`ALLOWED_CONTENT_TYPES`·`MAX_FILE_BYTES`)에서 파생하여 안내 문구와 실제 제약을
 * 항상 일치시킨다. 예) "JPEG / PNG / WEBP / GIF · 최대 10MB".
 */
export const UPLOAD_FORMAT_GUIDE = `${ALLOWED_CONTENT_TYPES.map(labelForContentType).join(' / ')} · 최대 ${Math.round(
  MAX_FILE_BYTES / MB,
)}MB`;

/**
 * ID57 — surface 별 권장 해상도(px)·비율 안내. 포맷/용량(UPLOAD_FORMAT_GUIDE)은 클라 검증
 * 상수에서 파생되어 항상 노출되고, 여기 값은 **권장**(강제 아님) 디자인 가이드다.
 *
 * 2026-10-06 쇼핑몰(storefront main 02d0c44) 실제 표시 규격과 대조해 맞췄다 — 비율은 쇼핑몰이 쓰는 비율,
 * 픽셀은 가장 크게 보이는 자리의 약 2배(고해상도 화면 대비). 기기마다 다르게 잘리는 곳은 "주요 내용 위치"를 함께 안내한다.
 *  - 상품 대표: 1:1 cover, 최대 600px(홈 강조 카드) — ProductCard / FeaturedPeekShelf
 *  - 상품 상세(갤러리): 1:1 틀, 잘리지 않고 여백(scale-down), 최대 618px — PdpGallery
 *  - 홈 배너: 모든 기기 1:1 cover(와이드 아님), 최대 480px, 모바일 별도 이미지 없음 — HeroBanner
 *  - 팝업스토어 썸네일: 640×360(16:9) — 고도몰 팝업 썸네일 규격(QA-351). 1:1 로 받으면 이관 썸네일 좌우가 잘렸다.
 *    FE 목록 카드도 16:9 로 맞추도록 전달(BO-TO-FE-2026-10-14-popup-thumbnail-16x9).
 *  - 이벤트: 목록 카드·상세 상단 모두 16:9 cover, 상세 최대 1200px — app/events
 *  - 오프라인 매장: 기기마다 다르게 잘림(모바일 약 3.8:1 · 태블릿/PC 0.9~2.1:1 · 1728px 이상 4:3) — HomeStoreSection
 *  - 펀딩 커버: PC 1:1 · 태블릿 2:1 · 모바일 3:2 로 잘림, PC 최대 약 763px — FundingDetail
 * 강제 아님(권장). 쇼핑몰 화면이 바뀌면 이 한 곳만 고치면 모든 업로더에 반영된다(SSoT).
 */
export const UPLOAD_DIMENSION_GUIDE: Partial<Record<MediaCategory, string>> = {
  PRODUCT_THUMBNAIL: '권장 1200×1200px · 1:1(정사각, 가장자리가 잘릴 수 있음)',
  PRODUCT_DETAIL: '권장 1200×1200px · 1:1(정사각이 아니면 잘리지 않고 여백이 생김)',
  CMS_BANNER: '권장 1000×1000px · 1:1(정사각) — 모든 기기 공통, 모서리가 둥글게 잘리니 주요 내용은 가운데에',
  CMS_POPUP: '권장 640×360px · 16:9 — 고도몰 팝업 썸네일과 같은 규격',
  CMS_EVENT: '권장 1920×1080px · 16:9 — 목록 카드와 상세 상단에 같은 이미지',
  CMS_NOTICE: '권장 가로 1200px · 세로 자유',
  OFFLINE_STORE: '권장 1600×1200px · 4:3 — 기기마다 가로로 길게 잘리니 주요 내용은 가운데 가로띠(약 1600×420)에',
  FUNDING_COVER: '권장 1600×1600px · 1:1 — 태블릿 2:1·모바일 3:2 로 잘리니 주요 내용은 가운데 가로띠(약 1600×800)에',
};

/** 펀딩 상세 이미지(갤러리) — 잘리지 않고 세로로 이어 붙여 보인다(PC 최대 약 1567px, 태블릿 1279px). */
export const FUNDING_GALLERY_GUIDE = '권장 가로 1280px · 세로 자유(잘리지 않고 세로로 이어 붙여 보임)';

/**
 * 에디터 본문(HTML) 이미지 — 이미지 버튼·붙여넣기·끌어다 놓기로 넣는 본문 이미지의 권장 규격.
 * 카드/대표 이미지(위 UPLOAD_DIMENSION_GUIDE)와 달리 본문 폭에 맞춰 늘어나므로 비율은 자유, 가로 폭만 권장한다.
 * 쇼핑몰 본문 폭: 상품 상세·이벤트 본문은 이미지 최대 1200px로 제한(PdpPanels·events), 팝업스토어 본문 1168px,
 * 공지·당첨자 발표는 제한 없이 본문 폭(최대 약 1567px)이지만 1200px 이면 충분히 선명하다. 작은 이미지는 늘리지 않고 원래 크기로 보인다.
 * 홈 팝업은 BO 의 가로·세로 크기가 곧 표시 크기라 homePopupImageGuide(width, height) 로 따로 만든다.
 */
export type EditorImageSurface =
  | 'PRODUCT_DETAIL_BODY'
  | 'NOTICE_BODY'
  | 'HOME_POPUP_BODY'
  | 'EVENT_BODY'
  | 'EVENT_WINNER_BODY'
  | 'POPUP_STORE_BODY'
  | 'EVER_TV_BODY'
  | 'FUNDING_BODY';

export const EDITOR_IMAGE_GUIDE: Record<EditorImageSurface, string> = {
  PRODUCT_DETAIL_BODY: '본문 이미지 권장 가로 1200px · 세로 자유',
  NOTICE_BODY: '본문 이미지 권장 가로 1200px · 세로 자유',
  HOME_POPUP_BODY: '본문 이미지 권장 가로 840px(기본 팝업 420px 의 2배) · 세로 자유',
  EVENT_BODY: '본문 이미지 권장 가로 1200px · 세로 자유',
  EVENT_WINNER_BODY: '본문 이미지 권장 가로 1200px · 세로 자유',
  POPUP_STORE_BODY: '본문 이미지 권장 가로 1200px · 세로 자유',
  EVER_TV_BODY: '본문 이미지 권장 가로 1200px · 세로 자유',
  FUNDING_BODY: '본문 이미지 권장 가로 1200px · 세로 자유',
};

const HOW_TO_UPLOAD = '이미지 버튼, 붙여넣기, 끌어다 놓기로 올릴 수 있습니다';

/**
 * 홈 팝업 본문 이미지 — 쇼핑몰 팝업 폭 = BO 가로(px), 비우면 420px(HomePopupLayer). 세로를 정하면 넘치는 부분은 잘린다.
 * 권장 가로는 표시 폭의 2배, 세로를 정했다면 같은 비율로.
 */
export const homePopupImageGuide = (width: string | number | null | undefined, height: string | number | null | undefined): string => {
  const w = Number(width) > 0 ? Number(width) : 420;
  const h = Number(height) > 0 ? Number(height) : null;
  const size = h
    ? `본문 이미지 권장 ${w * 2}×${h * 2}px(팝업 ${w}×${h}px 의 2배) · 세로를 넘는 부분은 잘림`
    : `본문 이미지 권장 가로 ${w * 2}px(팝업 가로 ${w}px 의 2배) · 세로 자유`;
  return `${size} · ${UPLOAD_FORMAT_GUIDE} · ${HOW_TO_UPLOAD}`;
};

/** 에디터 본문 이미지 안내(권장 폭 · 허용 형식·최대 용량 · 올리는 방법). */
export const editorImageGuideFor = (surface: EditorImageSurface): string =>
  `${EDITOR_IMAGE_GUIDE[surface]} · ${UPLOAD_FORMAT_GUIDE} · ${HOW_TO_UPLOAD}`;

/** surface(category) 에 정의된 권장 px/비율 안내를 반환(없으면 undefined → 포맷 가이드만 노출). */
export const dimensionGuideFor = (category: MediaCategory): string | undefined =>
  UPLOAD_DIMENSION_GUIDE[category];
